import { useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import type { DataTableProps } from '@/components/ui/data-table'
import { Tooltip } from '@/components/ui/tooltip'

export const ROW_SELECTION_COLUMN_ID = 'rowSelection' as const

function useRowSelection<TDataItem, TIdField extends keyof TDataItem>({
	idField,
	data,
	isSelectable,
	getDisabledReason
}: {
	idField: TIdField
	data?: TDataItem[]
	isSelectable?: (dataItem: TDataItem) => boolean
	getDisabledReason?: (dataItem: TDataItem) => string | undefined
}) {
	const [selected, setSelected] = useState<TDataItem[TIdField][]>([])
	const [selectedItemsMap, setSelectedItemsMap] = useState<
		Map<TDataItem[TIdField], TDataItem>
	>(new Map())

	const resetSelection = useCallback(() => {
		setSelected([])
		setSelectedItemsMap(new Map())
	}, [])

	const selectableIds = useMemo(
		() =>
			(data ?? [])
				.filter((item) => !isSelectable || isSelectable(item))
				.map((item) => item[idField]),
		[data, idField, isSelectable]
	)

	// Keep a snapshot of selected items across page changes: drop deselected,
	// add newly-selected ones found in the currently-loaded data.
	useEffect(() => {
		setSelectedItemsMap((prev) => {
			let changed = false
			const next = new Map(prev)
			for (const id of next.keys()) {
				if (!selected.includes(id)) {
					next.delete(id)
					changed = true
				}
			}
			for (const item of data ?? []) {
				const id = item[idField]
				if (selected.includes(id) && !next.has(id)) {
					next.set(id, item)
					changed = true
				}
			}
			return changed ? next : prev
		})
	}, [selected, data, idField])

	const selectedItems = useMemo(
		() =>
			selected.flatMap((id) => {
				const item = selectedItemsMap.get(id)
				return item ? [item] : []
			}),
		[selected, selectedItemsMap]
	)

	return {
		rowSelectionColumn: makeRowSelectionColumn({
			idField,
			selected,
			setSelected,
			selectableIds,
			isSelectable,
			getDisabledReason
		}),
		resetSelection,
		selected,
		selectedItems,
		selectionCount: selected.length
	}
}

function makeRowSelectionColumn<TDataItem, TIdField extends keyof TDataItem>({
	idField,
	selected,
	setSelected,
	selectableIds,
	isSelectable,
	getDisabledReason
}: {
	idField: TIdField
	selected: TDataItem[TIdField][]
	setSelected: (selected: TDataItem[TIdField][]) => void
	selectableIds: TDataItem[TIdField][]
	isSelectable?: (dataItem: TDataItem) => boolean
	getDisabledReason?: (dataItem: TDataItem) => string | undefined
}): DataTableProps<
	TDataItem,
	never,
	typeof ROW_SELECTION_COLUMN_ID
>['columns'][number] {
	const selectedSelectableCount = selectableIds.reduce(
		(count, id) => (selected.includes(id) ? count + 1 : count),
		0
	)
	const getHeaderCheckedState = (): boolean | 'indeterminate' => {
		if (selectableIds.length === 0 || selectedSelectableCount === 0) {
			return false
		}
		if (selectedSelectableCount === selectableIds.length) {
			return true
		}
		return 'indeterminate'
	}
	const headerChecked = getHeaderCheckedState()

	return {
		id: ROW_SELECTION_COLUMN_ID,
		header: (
			<Checkbox
				checked={headerChecked === true}
				disabled={selectableIds.length === 0}
				onChange={() => {
					if (headerChecked === false) {
						const merged = [...selected]
						for (const id of selectableIds) {
							if (!merged.includes(id)) {
								merged.push(id)
							}
						}
						setSelected(merged)
					} else {
						setSelected(
							selected.filter((id) => !selectableIds.includes(id))
						)
					}
				}}
				ref={(el) => {
					if (el) {
						el.indeterminate = headerChecked === 'indeterminate'
					}
				}}
			/>
		),
		cell: ({ dataItem }) => {
			const itemSelectable = !isSelectable || isSelectable(dataItem)
			const disabledReason = itemSelectable
				? undefined
				: getDisabledReason?.(dataItem)
			const checkbox = (
				<Checkbox
					checked={
						itemSelectable && selected.includes(dataItem[idField])
					}
					disabled={!itemSelectable}
					onChange={() => {
						setSelected(
							selected.includes(dataItem[idField])
								? selected.filter(
										(id) => id !== dataItem[idField]
									)
								: [...selected, dataItem[idField]]
						)
					}}
					onClick={(e) => e.stopPropagation()}
				/>
			)
			if (disabledReason) {
				return (
					<Tooltip content={disabledReason}>
						<span className='inline-flex'>{checkbox}</span>
					</Tooltip>
				)
			}
			return checkbox
		},
		size: 68
	}
}

export { useRowSelection }
